import { DatePipe, formatDate } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, LOCALE_ID, computed, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';

import {
  ChangeEntryView,
  ChangeFieldKind,
  ChangeFieldView,
  ChangeRecordModel,
  ChangeValue,
  GuaranteeModel,
  GuaranteesV2Service,
  PrescriptionsV2Service,
  V2PrescriptionModel,
  isDateValue,
  toChangeEntries,
} from '@lib/guarantees/data-access';
import { selectGuaranteesMemberId } from '@lib/guarantees/data-access/store/guarantees-v2.selectors';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Observable, distinctUntilChanged, take } from 'rxjs';

import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';

import { ActorNamePipe } from './actor-name.pipe';
import { CalloutComponent } from './callout.component';
import { ChipComponent } from './chip.component';
import { displayName } from './display-name';
import { CHANGE_OPERATION_ICON, CHANGE_OPERATION_LABEL_KEY, CHANGE_OPERATION_TONE, ChipTone, HISTORY_FIELD_LABEL_KEY, HISTORY_FIELD_VALUE_LABEL_KEY } from './guarantee-vocabulary';

/** Which record's past is being read, and therefore which endpoint answers for it. */
export type ChangeHistoryKind = 'guarantee' | 'prescription';

const TITLE_KEY: Record<ChangeHistoryKind, string> = {
  guarantee: 'guaranteeV2.action.guaranteeHistory',
  prescription: 'guaranteeV2.action.lineHistory',
};

const HINT_KEY: Record<ChangeHistoryKind, string> = {
  guarantee: 'guaranteeV2.action.guaranteeHistoryHint',
  prescription: 'guaranteeV2.action.lineHistoryHint',
};

const KIND_CLASSES: Record<ChangeFieldKind, string> = {
  added: 'text-success-700',
  removed: 'text-error-700',
  changed: 'text-neutral-700',
};

/** One field of one version, resolved into what is actually printed. */
interface ChangeRowView {
  key: string;
  label: string;
  /** True where the field has no name yet and the path is standing in for one. */
  unnamed: boolean;
  ordinal?: number;
  kind: ChangeFieldKind;
  before: string;
  after: string;
  summary: string;
  valueClass: string;
}

interface HistoryEntryView {
  key: string;
  version: number;
  timestamp: string;
  actorName?: string;
  operationLabelKey: string;
  operationIcon: string;
  operationTone: ChipTone;
  /** The oldest version held, which has nothing before it to have changed anything against. */
  isEarliest: boolean;
  rows: ChangeRowView[];
}

const NOT_STATED = '—';

/**
 * What has been done to one record, in the panel beside the state it produced — never a dialog that
 * covers it.
 *
 * Folded shut and fetched on first open: every version carries a whole snapshot.
 */
@Component({
  selector: 'guarantees-change-history',
  standalone: true,
  imports: [DatePipe, MatIcon, MatTooltip, TranslatePipe, ActorNamePipe, ChipComponent, CalloutComponent],
  templateUrl: './change-history.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChangeHistoryComponent {
  kind = input.required<ChangeHistoryKind>();
  /** The guarantee or the prescription, according to `kind`. */
  recordId = input.required<string>();

  private readonly guarantees = inject(GuaranteesV2Service);
  private readonly prescriptions = inject(PrescriptionsV2Service);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly locale = inject(LOCALE_ID);
  private readonly store = inject(Store);

  /** Whose record it is. A fact about the page rather than about the record, so it is not passed in. */
  private readonly memberId = this.store.selectSignal(selectGuaranteesMemberId);

  protected readonly open = signal(false);
  protected readonly loading = signal(false);
  protected readonly failed = signal(false);
  protected readonly loaded = signal(false);

  private readonly entries = signal<ChangeEntryView[]>([]);

  protected readonly titleKey = computed(() => TITLE_KEY[this.kind()]);
  protected readonly hintKey = computed(() => HINT_KEY[this.kind()]);

  protected readonly history = computed<HistoryEntryView[]>(() => {
    const entries = this.entries();

    return entries.map((entry, index) => ({
      key: `${entry.version}:${entry.timestamp}`,
      version: entry.version,
      timestamp: entry.timestamp,
      actorName: displayName(entry.actorName),
      operationLabelKey: CHANGE_OPERATION_LABEL_KEY[entry.operation] ?? '',
      operationIcon: CHANGE_OPERATION_ICON[entry.operation] ?? 'edit',
      operationTone: CHANGE_OPERATION_TONE[entry.operation] ?? 'neutral',
      isEarliest: index === entries.length - 1,
      rows: entry.fields.map((field, position) => this.toRow(field, position)),
    }));
  });

  constructor() {
    // A panel follows whichever record is selected rather than being reopened per record, so the
    // record can change underneath. What was fetched for the last one is then not history at all.
    toObservable(this.recordId)
      .pipe(distinctUntilChanged(), takeUntilDestroyed())
      .subscribe(() => {
        this.entries.set([]);
        this.loaded.set(false);
        this.failed.set(false);

        if (this.open()) {
          this.fetch();
        }
      });
  }

  protected toggle(): void {
    const open = !this.open();
    this.open.set(open);

    if (open && !this.loaded()) {
      this.fetch();
    }
  }

  protected retry(): void {
    this.fetch();
  }

  private fetch(): void {
    const memberId = this.memberId();
    const recordId = this.recordId();
    if (!memberId || !recordId) {
      return;
    }

    this.loading.set(true);
    this.failed.set(false);

    const request: Observable<ChangeRecordModel<GuaranteeModel | V2PrescriptionModel>[]> =
      this.kind() === 'guarantee' ? this.guarantees.getGuaranteeHistory(memberId, recordId) : this.prescriptions.getPrescriptionHistory(memberId, recordId);

    request.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe({
      next: records => {
        this.entries.set(toChangeEntries(records ?? []));
        this.loading.set(false);
        this.loaded.set(true);
      },
      error: () => {
        this.loading.set(false);
        this.failed.set(true);
      },
    });
  }

  private toRow(field: ChangeFieldView, index: number): ChangeRowView {
    const labelKey = HISTORY_FIELD_LABEL_KEY[field.path];

    return {
      key: `${field.path}:${field.ordinal ?? 0}:${field.kind}:${index}`,
      label: labelKey ? this.translate.instant(labelKey) : field.path,
      unnamed: !labelKey,
      ordinal: field.ordinal,
      kind: field.kind,
      before: this.print(field.path, field.before),
      after: this.print(field.path, field.after),
      summary: field.summary ?? NOT_STATED,
      valueClass: KIND_CLASSES[field.kind],
    };
  }

  /**
   * A stored value as it is read rather than as it is held: a state by its name, a moment by its
   * date, a flag as yes or no. An unstated value is dashed rather than left blank, because half of
   * every change is a value that was not there before.
   */
  private print(path: string, value: ChangeValue): string {
    if (value === null || value === undefined || value === '') {
      return NOT_STATED;
    }

    if (typeof value === 'boolean') {
      return this.translate.instant(value ? 'general.label.yes' : 'general.label.no');
    }

    if (typeof value === 'number') {
      return String(value);
    }

    const valueKey = HISTORY_FIELD_VALUE_LABEL_KEY[path]?.[value];
    if (valueKey) {
      return this.translate.instant(valueKey);
    }

    return isDateValue(value) ? formatDate(value, 'dd.MM.yy HH:mm', this.locale) : value;
  }
}
